import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from "../../../components/ui/Button";
import { Calculator, Save, CheckCircle2, FileX, FileText, FileSpreadsheet, Download, ExternalLink, ArrowRight, Eye, Package, Plus } from "lucide-react";
import { IndianRupee } from "lucide-react";
import { fetchPurchases } from "../../inventory/pages/InventoryPage/services";
import { PurchasesTable } from "../../inventory/components/PurchasesTable";
import { ItemDetailsModal } from "../../inventory/components/ItemDetailsContent";
import { ResizableSplitPane } from "../components/ResizableSplitPane";
import { BudgetSearchFilter } from "../components/BudgetSearchFilter";
import { PdfViewer } from "../components/pdfviewer";
import { QuotationInfo } from "../components/quotation_info";
import { InitialQuotationMaker } from "../components/initial_quotation_maker";
import { QuotationPreviewModal } from "../components/quotation Preview";
import { XlViewer } from "../components/xl_viewer";

import { documentService } from "../../../services/documentService";
import { API_BASE_URL } from "../../../services/api";
import { format } from "date-fns";
import { Tabs } from "../../../components/ui/Tabs";
import { BUDGET_MAIN_TABS } from "../components/budget_tabs";
import { budgetQuotationService } from './../services/budgetQuotationService';
import { toast } from 'react-hot-toast';
import { ContactModals } from "../../verve-contacts/components/ContactModals";
import { useContactActivities } from "../../verve-contacts/hooks/useContactActivities";
import { useAuthStore } from "@/store/authStore";
import { BudgetTableView } from "../components/budget_table_view";
import ClientsTable from "../components/clients_table";
import OrderingScreen from "../../ordering/screen/OrderingScreen";

const getContactRefDisplay = (contact) => {
  if (!contact) return '';
  const refSource = contact.referenceType || contact.leadId?.referenceType || (contact.source && contact.source !== 'Manual' ? contact.source : '') || '';
  if (refSource) return refSource;
  return contact.referenceType || contact.source || '-';
};

export const BudgetScreen = () => {
  const { user } = useAuthStore();
  const [budget, setBudget] = useState({
    blum: null,
    carcase: null,
    europlak: null,
  });

  // Client Modal State
  const [detail, setDetail] = useState(null);
  const [actNote, setActNote] = useState("");
  const { activities, addActivity } = useContactActivities(detail?._id);
  const [viewPurchase, setViewPurchase] = useState(null);
  // Stock selection state: { _key: { flatItem, qty } }
  const [stockSelections, setStockSelections] = useState({});

  const [customTableTotal, setCustomTableTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("Catalog");

  const [catalogDocs, setCatalogDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [initialPdfDoc, setInitialPdfDoc] = useState(null);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [completedExecutionReturnId, setCompletedExecutionReturnId] = useState(null);

  const [quotationDetails, setQuotationDetails] = useState({
    projectName: '',
    site: '',
    contactRef: '',
    date: '',
    quotationNo: '',
    referenceDesigner: ''
  });

  const [quotationTableData, setQuotationTableData] = useState({
    rows: [],
    subTotal: 0,
    discountAmount: 0,
    gstAmount: 0,
    grandTotal: 0,
    discountType: 'percentage',
    discountValue: 0,
    gstType: 'percentage',
    gstValue: 0
  });

  const [showPreview, setShowPreview] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState(() => {
    return location.state?.startPrep || location.state?.openForLead ? 'prep' : 'view';
  });
  const [selectedRowForOrdering, setSelectedRowForOrdering] = useState(null);

  // When navigated from a Lead Execution detail (Create Budget/Ordering), check states and prepopulate
  useEffect(() => {
    if (location.state?.highlightLeadId) {
      setHighlightedRowId(location.state.highlightLeadId);
    }
    if (location.state?.startPrep || location.state?.openForLead) {
      const existingQuotation = location.state.editQuotation;
      const contact = typeof location.state.openForLead === 'object' && location.state.openForLead !== null && Object.keys(location.state.openForLead).length > 0
        ? location.state.openForLead
        : (existingQuotation?.contactObj || {});
      const fullName = contact.customerName || contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || "";
      const phone = contact.phoneNumber || contact.phone || contact.mobile || "";
      const nameWithPhone = phone ? `${fullName} - ${phone}` : fullName;
      const defaultSite = contact.city ? `${contact.city} ${contact.state || ''}`.trim() : (contact.site || contact.siteName || '');

      const loggedInUserName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '';

      if (existingQuotation) {
        let projName = existingQuotation.projectName;
        if (!projName || projName === fullName || projName === contact.customerName) {
          projName = nameWithPhone;
        }

        let resolvedContactRef = '';
        if (contact && Object.keys(contact).length > 0) {
          resolvedContactRef = getContactRefDisplay(contact);
        }
        if (!resolvedContactRef || resolvedContactRef === '-' || resolvedContactRef === 'Manual') {
          const qRef = existingQuotation.contactRef || '';
          if (!/^[0-9a-fA-F]{24}$/.test(qRef) && !/^ver-/i.test(qRef) && !/^vk/i.test(qRef) && qRef !== contact.customId && qRef !== contact.verveId) {
            resolvedContactRef = qRef;
          } else {
            resolvedContactRef = getContactRefDisplay(contact) || '-';
          }
        }

        let resolvedDesigner = existingQuotation.referenceDesigner || '';
        if (!resolvedDesigner || /^[0-9a-fA-F]{24}$/.test(resolvedDesigner)) {
          resolvedDesigner = loggedInUserName;
        }

        setQuotationDetails({
          _id: existingQuotation._id,
          projectName: projName,
          site: existingQuotation.site || defaultSite,
          contactRef: resolvedContactRef || '-',
          leadId: contact._id || location.state.highlightLeadId || null,
          clientId: contact._id || location.state.highlightLeadId || null,
          date: existingQuotation.date || '',
          quotationNo: existingQuotation.quotationNo || '',
          referenceDesigner: resolvedDesigner,
          isInitial: Boolean(existingQuotation.quotationType === 'Initial' || existingQuotation.type === 'initial_quotation' || existingQuotation.isInitial || (existingQuotation.quotationNo && typeof existingQuotation.quotationNo === 'string' && existingQuotation.quotationNo.startsWith('VK20') && existingQuotation.quotationNo.charAt(6) === 'I'))
        });

        const rows = Array.isArray(existingQuotation.items || existingQuotation.rows)
          ? (existingQuotation.items || existingQuotation.rows).map((r, i) => ({
            id: r.id || i + 1,
            particulars: r.name || r.particulars || '',
            quantity: r.quantity || '',
            design: r.design || '',
            type: r.isHorizontal ? 'horizontal' : (r.type || 'item'),
            colourType: r.colour || r.colourType || '',
            warranty: r.warranty || '',
            amount: r.amount || 0,
            _fromStock: Boolean(r._fromStock)
          }))
          : [];

        const discountStr = (existingQuotation.totals?.discount || "0").replace(/[^0-9.]/g, '');
        const gstStr = (existingQuotation.totals?.gst || "0").replace(/[^0-9.]/g, '');

        setQuotationTableData(prev => ({
          ...prev,
          rows: rows.length ? rows : [{ id: 1, particulars: '', design: '', type: 'item', colourType: '', warranty: '', amount: 0 }],
          discountValue: Number(discountStr),
          discountType: existingQuotation.totals?.discountType || 'amount',
          gstValue: Number(gstStr),
          gstType: existingQuotation.totals?.gstType || 'amount'
        }));
      } else {
        const isInitQuote = Boolean(
          location.state?.isInitial ||
          location.state?.quotationType === 'Initial' ||
          ['initial_quotation', 'initial quotation', 'initial', 'quote', 'quotation', 'lead', 'design_proposal', 'design proposal'].includes(String(contact?.status || contact?.leadStatus || contact?.quoteStatus || contact?.currentStage || contact?.stage || contact?.teleStatus || contact?.projectStatus || '').toLowerCase().trim())
        );

        setQuotationDetails(prev => ({
          ...prev,
          projectName: nameWithPhone,
          site: defaultSite,
          contactRef: getContactRefDisplay(contact) || '-',
          leadId: contact._id || location.state.highlightLeadId || null,
          clientId: contact._id || location.state.highlightLeadId || null,
          _id: undefined,
          quotationNo: '',
          referenceDesigner: loggedInUserName,
          isInitial: isInitQuote
        }));
        setQuotationTableData({
          rows: [],
          subTotal: 0,
          discountAmount: 0,
          gstAmount: 0,
          grandTotal: 0,
          discountType: 'percentage',
          discountValue: 0,
          gstType: 'percentage',
          gstValue: 0
        });
      }
      setActiveMainTab('prep');
    }
  }, [location.state]);

  const handleCancelQuotation = () => {
    if (location.state?.returnToExecutionId) {
      navigate(`/lead-executions/${location.state.returnToExecutionId}`);
      return;
    }
    setQuotationDetails({
      projectName: '',
      site: '',
      contactRef: '',
      date: quotationDetails.date, // keep generated date
      quotationNo: quotationDetails.quotationNo, // keep generated no
      referenceDesigner: ''
    });
    setActiveMainTab('view');
  };

  const handleAddStockItem = (doc, qty = 1) => {
    // Support both purchase-master fields and purchase-transaction item fields
    const itemRef = doc.item || doc.itemRef || {};
    const rate = Number(doc.finalNetPayment) || Number(doc.netBill) || Number(doc.blumPrice) || Number(doc.vervePrice) || Number(doc.rate) || Number(doc.price) || Number(doc.amount) || Number(doc.netAmount) || 0;
    const desc = doc.itemName || itemRef.itemName || itemRef.name || doc.description || doc.articleId || doc.name || doc.title || doc.itemDetails || 'Stock Item';
    const vendorName = doc.vendor || doc.vendorName || (typeof doc.inv?.vendor === 'object' ? (doc.inv?.vendor?.vendorName || doc.inv?.vendor?.name) : doc.inv?.vendor) || '';
    const invoiceNo = doc.invoiceNo || doc.inv?.invoiceNumber || doc.inv?.invoiceNo || '';

    const newRow = {
      id: Date.now() + Math.random(),
      type: 'item',
      particulars: desc,
      quantity: String(qty),
      colourType: vendorName,
      warranty: invoiceNo,
      amount: rate
    };

    setQuotationTableData(prev => ({
      ...prev,
      rows: [...(prev.rows || []), newRow]
    }));
    toast.success(`Added "${desc}" to quotation`);
  };

  const handleAddSelectedToQuotation = () => {
    const selectedList = Object.values(stockSelections);
    if (selectedList.length === 0) return;
    const newRows = selectedList.map(({ flatItem, qty }) => ({
      id: Date.now() + Math.random(),
      type: 'item',
      _fromStock: true,
      particulars: flatItem.itemName,
      quantity: String(qty || 1),
      colourType: flatItem.vendor,
      warranty: flatItem.invoiceNo,
      amount: (flatItem.purchaseRate * (qty || 1)) || flatItem.amount
    }));
    setQuotationTableData(prev => ({
      ...prev,
      rows: [...(prev.rows || []), ...newRows]
    }));
    setStockSelections({});
    toast.success(`Added ${selectedList.length} item(s) to quotation`);
  };

  // Auto-sync stock selections → Budget Summary quotation rows (live, real-time)
  useEffect(() => {
    const stockRows = Object.values(stockSelections).map(({ flatItem, qty }) => ({
      id: `stock-${flatItem._key}`,   // stable id so table doesn't flicker
      type: 'item',
      _fromStock: true,
      particulars: flatItem.itemName,
      quantity: String(qty || 1),
      colourType: '',
      warranty: '',
      amount: (flatItem.purchaseRate * (qty || 1)) || flatItem.amount
    }));

    setQuotationTableData(prev => {
      // Keep manually added rows; replace all _fromStock rows
      const manualRows = (prev.rows || []).filter(r => !r._fromStock);
      return { ...prev, rows: [...manualRows, ...stockRows] };
    });
  }, [stockSelections]);

  const handleSubmitQuotation = async (pdfBlob, sendToOwner) => {
    const rows = quotationTableData.rows;
    const hasValidItem = rows.some(r => {
      const name = (r.text || r.particulars || '').trim();
      const amt = Number(r.amount);
      return name && amt > 0;
    });
    if (!hasValidItem) {
      toast.error('Add at least one item with a name and amount greater than 0.');
      return;
    }
    try {
      const dataToSave = {
        _id: quotationDetails._id,
        projectName: quotationDetails.projectName,
        clientName: quotationDetails.projectName,
        site: quotationDetails.site,
        contactRef: quotationDetails.contactRef,
        contactId: quotationDetails.leadId || quotationDetails.clientId,
        date: quotationDetails.date,
        quotationNo: quotationDetails.quotationNo,
        referenceDesigner: quotationDetails.referenceDesigner,
        rows: quotationTableData.rows
          .filter(r => {
            const name = (r.text || r.particulars || '').trim();
            const amt = Number(r.amount);
            return r.type === 'horizontal' || (name && amt > 0);
          })
          .map(r => ({
            name: r.text || r.particulars || '',
            quantity: r.quantity || '',
            type: r.type || 'item',
            colour: r.colourType || '',
            warranty: r.warranty || '',
            vendor: r.vendor || '',
            amount: r.amount?.toString() || '',
            details: (r.particulars || '').split('\n').slice(1),
            isHorizontal: r.type === 'horizontal',
            _fromStock: Boolean(r._fromStock)
          })),
        totals: {
          total: `₹${quotationTableData.subTotal}`,
          discount: `₹${quotationTableData.discountAmount}`,
          gst: `₹${quotationTableData.gstAmount}`,
          grandTotal: `₹${quotationTableData.grandTotal}`,
          discountType: quotationTableData.discountType,
          discountValue: quotationTableData.discountValue,
          discountLabel: quotationTableData.discountValue && Number(quotationTableData.discountValue) !== 0 ? (quotationTableData.discountType === 'percentage' ? `(${quotationTableData.discountValue}%)` : `(₹${Number(quotationTableData.discountValue).toLocaleString('en-IN')})`) : '',
          gstType: quotationTableData.gstType,
          gstValue: quotationTableData.gstValue,
          gstLabel: quotationTableData.gstValue && Number(quotationTableData.gstValue) !== 0 ? (quotationTableData.gstType === 'percentage' ? `(${quotationTableData.gstValue}%)` : `(₹${Number(quotationTableData.gstValue).toLocaleString('en-IN')})`) : ''
        },
        status: sendToOwner ? 'Pending' : 'Draft',
        isInitial: quotationDetails.isInitial || false
      };

      const res = await budgetQuotationService.saveQuotation(dataToSave);
      const savedId = res.data._id;

      await budgetQuotationService.uploadPdf(savedId, pdfBlob, sendToOwner ? 'Pending' : 'Draft');

      toast.success(sendToOwner ? 'Budget submitted to Owner successfully!' : 'Budget saved successfully!');
      setShowPreview(false);
      if (location.state?.returnToExecutionId) {
        setCompletedExecutionReturnId(location.state.returnToExecutionId);
      } else {
        setActiveMainTab('view');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit budget quotation');
    }
  };

  useEffect(() => {
    const fetchCatalogDocs = async () => {
      if (filterType === 'Stock') {
        setLoadingDocs(true);
        try {
          const res = await fetchPurchases();
          const pList = res?.data?.data || res?.data || (Array.isArray(res) ? res : []);
          const filtered = Array.isArray(pList) ? pList.filter(item => {
            if (!searchQuery) return true;
            const q = searchQuery.toLowerCase();
            const vendorName = item.vendorName || (typeof item.vendor === 'object' ? (item.vendor?.vendorName || item.vendor?.name) : item.vendor) || '';
            const itemName = item.item?.itemName || item.item?.name || item.description || '';
            const invoiceNo = item.invoiceNumber || item.invoiceNo || '';
            return vendorName.toLowerCase().includes(q) || itemName.toLowerCase().includes(q) || invoiceNo.toLowerCase().includes(q);
          }) : [];
          setCatalogDocs(filtered);
        } catch (err) {
          console.error("Failed to fetch purchases:", err);
          setCatalogDocs([]);
        } finally {
          setLoadingDocs(false);
        }
        return;
      }
      setLoadingDocs(true);
      try {
        const response = await documentService.getDocuments({
          isRelatedToCatalog: true,
          category: 'Budget',
          search: searchQuery,
          formatFilter: filterType === 'Catalog' ? 'All' : filterType,
        });
        if (response.success) {
          setCatalogDocs(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch catalog documents:", error);
      } finally {
        setLoadingDocs(false);
      }
    };

    fetchCatalogDocs();
  }, [searchQuery, filterType]);

  useEffect(() => {
    if (activeMainTab === 'prep' && quotationDetails.contactRef) {
      const fetchInitial = async () => {
        try {
          const res = await budgetQuotationService.getQuotations();
          if (res.success && res.data) {
            const found = res.data.find(q =>
              (q.quotationType === 'Initial' || q.type === 'initial_quotation' || q.quotationNo?.includes('VK/INT')) &&
              (
                q.clientId === quotationDetails.contactRef ||
                q.contactRef === quotationDetails.contactRef ||
                (q.clientId && q.clientId._id === quotationDetails.contactRef) ||
                q.customId === quotationDetails.contactRef
              )
            );
            if (found && found.pdfUrl) {
              setInitialPdfDoc({
                fileUrl: found.pdfUrl.startsWith('http') ? found.pdfUrl : `${API_BASE_URL}${found.pdfUrl}`,
                title: `InitialQuotation_${found.quotationNo || 'VK'}.pdf`,
                fileType: 'pdf'
              });
            } else {
              setInitialPdfDoc(null);
            }
          }
        } catch (e) {
          console.error("Failed to fetch initial quotation pdf", e);
        }
      };
      fetchInitial();
    }
  }, [activeMainTab, quotationDetails.contactRef]);

  useEffect(() => {
    const handleReset = () => setActiveMainTab('master');
    window.addEventListener('reset-budget-tab', handleReset);
    return () => window.removeEventListener('reset-budget-tab', handleReset);
  }, []);

  const totalBudget = Object.values(budget).reduce((acc, curr) => acc + (curr || 0), 0) + customTableTotal;
  const allSelected = false; // We removed the static logic for now

  const selectedBlum = null;
  const selectedCarcase = null;
  const selectedEuroplak = null;

  const SummaryItem = ({ title, item }) => {
    if (!item) return null;
    return (
      <div className="flex justify-between items-start bg-white p-3 rounded-lg border border-slate-100 shadow-sm">
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{title}</span>
          <span className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
            <CheckCircle2 size={14} className="text-emerald-500" />
            {item.label}
          </span>
        </div>
        <span className="text-sm font-bold text-slate-700">₹{item.price.toLocaleString('en-IN')}</span>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 w-full max-w-full min-h-[calc(100vh-120px)]">
      {activeMainTab !== 'ordering' && activeMainTab !== 'prep' && (
        <div
          onClick={() => setActiveMainTab('view')}
          className="relative overflow-hidden w-full h-32 bg-white border border-slate-200 rounded-xl flex items-center justify-between shadow-sm mb-2 cursor-pointer hover:border-slate-300 transition-all"
          title="Return to Quote & orders Master"
        >
          <div className="pl-8 z-20">
            <h1 className="font-serif font-medium text-3xl text-slate-900 tracking-wide">
              Quote & orders
            </h1>
            <p className="text-sm font-body text-slate-400 mt-2 font-medium">Manage Quotes & Orders</p>
          </div>
          <div className="w-[50%] h-full relative overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-transparent z-10 w-48" />
            <img
              src="/kitchens/1.jpg"
              alt="Quote & orders"
              className="absolute top-0 right-0 w-full h-full object-cover object-center opacity-90"
            />
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-200 overflow-hidden mb-2">
        <Tabs
          tabs={
            activeMainTab === 'ordering'
              ? [{ id: 'view', label: 'Master' }, { id: 'ordering', label: 'Order Prep' }]
              : activeMainTab === 'prep'
                ? BUDGET_MAIN_TABS
                : BUDGET_MAIN_TABS.filter(t => t.id !== 'prep')
          }
          activeTab={activeMainTab}
          onChange={setActiveMainTab}
          variant="main"
          className="w-full !border-b-0"
        />
      </div>

      {activeMainTab === 'prep' ? (
        <ResizableSplitPane
          initialLeftWidth={30}
          extraHeaderActions={
            <button
              onClick={() => {
                if (initialPdfDoc) {
                  setSelectedDocument(initialPdfDoc);
                } else {
                  toast.error("Initial Quotation not prepared or PDF missing for this client.");
                }
              }}
              className="p-1.5 bg-transparent rounded-md text-brand-blue hover:bg-slate-100 transition-colors"
              title="View Initial Quotation PDF"
            >
              <Eye size={16} />
            </button>
          }
          leftContent={
            <div className="flex flex-col gap-2 h-full">
              <BudgetSearchFilter
                searchTerm={searchQuery}
                onSearchChange={setSearchQuery}
                filterType={filterType}
                onFilterChange={setFilterType}
              />

              <div className={`flex-1 border border-slate-200 rounded-lg bg-white overflow-hidden shadow-sm flex flex-col ${filterType !== 'Stock' && (loadingDocs || (catalogDocs.length === 0 && !selectedDocument)) ? 'items-center justify-center text-center p-8' : ''}`}>
                {loadingDocs ? (
                  <div className="flex items-center justify-center p-8 text-slate-500">Loading {filterType === 'Stock' ? 'purchases' : 'catalog files'}...</div>
                ) : filterType === 'Stock' ? (
                  catalogDocs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-8 text-center">
                      <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                        <FileX className="text-slate-300" size={32} />
                      </div>
                      <h3 className="text-lg font-medium text-slate-800">No purchases found</h3>
                    </div>
                  ) : (() => {
                    // Flatten: one row per item in purchase.items[]
                    // Backend populates: vendor.vendorName, store.storeName,
                    //   items[].item.itemName, items[].item.units.unitName,
                    //   items[].quantity (purchased), items[].availableQty (current stock),
                    //   items[].purchaseRate, items[].netBill
                    const flatItems = catalogDocs.flatMap(inv => {
                      const vendor =
                        (typeof inv.vendor === 'object' ? (inv.vendor?.vendorName || inv.vendor?.name) : inv.vendor) ||
                        inv.vendorName || 'Unknown Vendor';
                      const invoiceNo = inv.invoiceNo || inv.invoiceNumber || '';
                      const date = (inv.invoiceDate || inv.createdAt)
                        ? new Date(inv.invoiceDate || inv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '';

                      // Primary: items[] array from backend
                      if (inv.items && inv.items.length > 0) {
                        return inv.items.map((it, idx) => ({
                          _key: `${inv._id}-${idx}`,
                          inv,
                          itemName: it.item?.itemName || it.item?.name || it.description || '—',
                          vendor,
                          invoiceNo,
                          date,
                          status: inv.status || 'Completed',
                          availableQty: Number(it.availableQty ?? it.quantity ?? 0),
                          purchasedQty: Number(it.quantity ?? 0),
                          unit: it.item?.units?.unitName || it.item?.unit?.unitName || '',
                          purchaseRate: Number(it.purchaseRate || 0),
                          amount: Number(it.netBill || it.baseAmount || 0)
                        }));
                      }
                      // Fallback: single item field
                      return [{
                        _key: inv._id,
                        inv,
                        itemName: inv.item?.itemName || inv.item?.name || inv.description || '—',
                        vendor,
                        invoiceNo,
                        date,
                        status: inv.status || 'Completed',
                        availableQty: Number(inv.quantity ?? 0),
                        purchasedQty: Number(inv.quantity ?? 0),
                        unit: inv.item?.units?.unitName || '',
                        purchaseRate: 0,
                        amount: Number(inv.finalNetPayment || inv.netBill || 0)
                      }];
                    }).filter(item => item.status !== 'Queue' && !item.inv?.isQueue);

                    const selectedCount = Object.keys(stockSelections).length;

                    return (
                      <div className="flex flex-col h-full">
                        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
                          {flatItems.map((flatItem) => {
                            const isSelected = !!stockSelections[flatItem._key];
                            const currentQty = stockSelections[flatItem._key]?.qty ?? 1;
                            const isOverStock = isSelected && flatItem.availableQty > 0 && Number(currentQty) > flatItem.availableQty;

                            return (
                              <div
                                key={flatItem._key}
                                onClick={() => {
                                  setStockSelections(prev => {
                                    const next = { ...prev };
                                    if (next[flatItem._key]) { delete next[flatItem._key]; }
                                    else { next[flatItem._key] = { flatItem, qty: 1 }; }
                                    return next;
                                  });
                                }}
                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-all select-none ${isSelected ? 'border-l-2' : 'hover:bg-slate-50 border-l-2 border-transparent'
                                  }`}
                                style={isSelected ? { backgroundColor: '#eff6ff', borderLeftColor: '#2563eb' } : {}}
                              >
                                {/* Circle selector — iOS style tick */}
                                <div
                                  className="w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center transition-all duration-150"
                                  style={isSelected
                                    ? { backgroundColor: '#2563eb', borderColor: '#2563eb' }
                                    : { backgroundColor: 'white', borderColor: '#cbd5e1' }
                                  }
                                >
                                  {isSelected && (
                                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                  )}
                                </div>

                                {/* Item info */}
                                <div className="flex flex-col min-w-0 flex-1">
                                  <span className={`text-[13px] font-semibold truncate ${isSelected ? 'text-brand-blue' : 'text-slate-800'}`} title={flatItem.itemName}>
                                    {flatItem.itemName}
                                  </span>
                                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                                    <span className="text-[11px] font-medium text-slate-500 truncate">{flatItem.vendor}</span>
                                    {flatItem.status === 'Queue' && (
                                      <><span className="text-slate-300 text-[10px]">•</span><span className="text-[10px] font-bold text-amber-700 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">Queue</span></>
                                    )}
                                    {flatItem.invoiceNo && <><span className="text-slate-300 text-[10px]">•</span><span className="text-[10px] font-bold text-brand-blue bg-blue-50 px-1.5 py-0.5 rounded">{flatItem.invoiceNo}</span></>}
                                    {flatItem.date && <><span className="text-slate-300 text-[10px]">•</span><span className="text-[11px] text-slate-400">{flatItem.date}</span></>}
                                    {flatItem.availableQty > 0 && (
                                      <><span className="text-slate-300 text-[10px]">•</span>
                                        <span className={`text-[11px] font-bold ${isOverStock ? 'text-red-500' : 'text-emerald-600'}`}>
                                          {flatItem.availableQty} {flatItem.unit} Stock
                                        </span></>
                                    )}
                                  </div>
                                </div>

                                {/* Right: rate + qty input — always visible, stopPropagation so typing doesn't toggle row */}
                                <div className="flex items-center gap-3 shrink-0" onClick={e => e.stopPropagation()}>
                                  {flatItem.purchaseRate > 0 && (
                                    <div className="flex items-center gap-0.5 text-[12px] font-bold text-slate-600">
                                      <IndianRupee size={11} className="text-slate-400" />
                                      <span>{Number(flatItem.purchaseRate).toLocaleString('en-IN')}</span>
                                      <span className="text-[10px] text-slate-400 font-normal">/pc</span>
                                    </div>
                                  )}
                                  {/* Qty input always visible */}
                                  <div className="flex flex-col items-center gap-0.5">
                                    <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-wide">Qty</span>
                                    <input
                                      type="number"
                                      min="0"
                                      value={isSelected ? currentQty : ''}
                                      placeholder="0"
                                      onChange={(e) => {
                                        const val = Number(e.target.value);
                                        if (val <= 0) {
                                          // qty 0 → auto-deselect
                                          setStockSelections(prev => {
                                            const next = { ...prev };
                                            delete next[flatItem._key];
                                            return next;
                                          });
                                        } else {
                                          // qty 1+ → auto-select or update existing
                                          setStockSelections(prev => ({
                                            ...prev,
                                            [flatItem._key]: { ...(prev[flatItem._key] || { flatItem }), qty: val }
                                          }));
                                        }
                                      }}
                                      className="w-14 text-center px-1 py-1 text-[12px] font-bold border rounded-lg outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none focus:ring-2"
                                      style={
                                        isOverStock
                                          ? { borderColor: '#f87171', backgroundColor: '#fef2f2', color: '#dc2626' }
                                          : isSelected
                                            ? { borderColor: '#2563eb', backgroundColor: 'white', color: '#2563eb' }
                                            : { borderColor: '#e2e8f0', backgroundColor: 'white', color: '#64748b' }
                                      }
                                      title={isOverStock ? `Exceeds available stock (${flatItem.availableQty})` : 'Enter quantity to select'}
                                    />
                                    {isOverStock && (
                                      <span className="text-[9px] text-red-500 font-bold">Max {flatItem.availableQty}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()
                ) : selectedDocument ? (
                  selectedDocument.fileType?.includes('pdf') ? (
                    <PdfViewer
                      selectedDocument={selectedDocument}
                      onClose={() => setSelectedDocument(null)}
                    />
                  ) : (
                    <XlViewer
                      selectedDocument={selectedDocument}
                      onClose={() => setSelectedDocument(null)}
                    />
                  )
                ) : catalogDocs.length === 0 ? (
                  <>
                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                      <FileX className="text-slate-300" size={32} />
                    </div>
                    <h3 className="text-lg font-medium text-slate-800">No documents found</h3>
                  </>
                ) : (
                  <div className="flex-1 overflow-y-auto w-full divide-y divide-slate-100">
                    {catalogDocs.map((doc) => (
                      <div
                        key={doc._id}
                        onClick={(e) => {
                          if (e.target.closest('a')) return;
                          const isPdf = doc.fileType?.includes('pdf');
                          const isExcel = doc.fileType?.includes('spreadsheet') || doc.fileType?.includes('excel') || doc.fileType?.includes('csv') || doc.fileName?.endsWith('.xlsx') || doc.fileName?.endsWith('.xls');
                          if (isPdf || isExcel) {
                            setSelectedDocument(doc);
                          } else if (doc.fileUrl) {
                            const token = localStorage.getItem('blum_token');
                            const relativeUrl = doc.fileUrl.startsWith('http') ? doc.fileUrl.replace(/^.*\/\/[^\/]+/, '') : doc.fileUrl;
                            const apiPath = relativeUrl.startsWith('/api') ? relativeUrl : `/api${relativeUrl}`;
                            window.open(`${API_BASE_URL}${apiPath}?token=${token}`, '_blank');
                          }
                        }}
                        className="p-4 hover:bg-slate-50 transition-colors flex items-center justify-between group cursor-pointer"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${doc.fileType?.includes('pdf') ? 'bg-amber-100 text-amber-600' : 'bg-emerald-100 text-emerald-600'}`}>
                            {doc.fileType?.includes('pdf') ? <FileText size={20} /> : <FileSpreadsheet size={20} />}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-semibold text-slate-800 truncate">{doc.title}</span>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500 truncate">{doc.category || 'Uncategorized'}</span>
                              <span className="text-xs text-slate-300">•</span>
                              <span className="text-xs text-slate-400 whitespace-nowrap">{format(new Date(doc.createdAt), 'MMM dd, yyyy')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          }
          rightContent={
            <div className="flex flex-col min-h-[400px] h-full">
              <h3 className="text-xl font-medium text-slate-800 mb-2 border-b border-slate-200 pb-2 flex items-center gap-2">
                Budget Summary
              </h3>

              <div className="flex flex-col gap-3 flex-1 overflow-y-auto pr-2">
                {/* !selectedBlum && !selectedCarcase && !selectedEuroplak && (
                <div className="flex flex-col items-center justify-center h-32 text-center">
                  <p className="text-sm text-slate-400">Select options on the left to build your estimate.</p>
                </div>
              ) */}

                <SummaryItem title="Blum Package" item={selectedBlum} />
                <SummaryItem title="Carcase Material" item={selectedCarcase} />
                <SummaryItem title="Europlak Finish" item={selectedEuroplak} />
                <div className="">
                  <InitialQuotationMaker
                    key={quotationDetails._id || quotationDetails.contactRef || 'new'}
                    onTotalChange={setCustomTableTotal}
                    onDataChange={setQuotationTableData}
                    details={quotationDetails}
                    setDetails={setQuotationDetails}
                    initialRows={quotationTableData.rows}
                    initialDiscount={{ type: quotationTableData.discountType, value: quotationTableData.discountValue }}
                    initialGst={{ type: quotationTableData.gstType, value: quotationTableData.gstValue }}
                  />
                </div>
              </div>

              <div className="mt-8 pt-5 border-t border-slate-200">


                <div className="flex flex-col xl:flex-row justify-end gap-3 transition-all">
                  <Button variant="secondary" className="w-full xl:w-auto" onClick={handleCancelQuotation}>
                    Cancel
                  </Button>
                  <Button
                    variant="orange"
                    className="w-full xl:w-auto"
                    onClick={() => {
                      if (!quotationDetails.projectName || !quotationDetails.projectName.trim()) {
                        toast.error("Project Name is required!");
                        return;
                      }
                      setShowPreview(true);
                    }}
                    rightIcon={<ArrowRight size={16} />}
                  >
                    Preview
                  </Button>
                  {allSelected && (
                    <>
                      <Button variant="secondary" className="w-full xl:w-auto" onClick={() => setBudget({ blum: null, carcase: null, europlak: null })}>
                        Reset
                      </Button>
                      <Button className="bg-brand-blue text-white hover:bg-blue-700 w-full xl:w-auto" leftIcon={<Save size={16} />}>
                        Save Budget
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Full Screen Preview Modal */}
              {showPreview && (
                <QuotationPreviewModal
                  onClose={() => setShowPreview(false)}
                  onSubmit={handleSubmitQuotation}
                  data={{
                    projectName: quotationDetails.projectName || "-",
                    clientName: quotationDetails.projectName || "-",
                    date: quotationDetails.date || "-",
                    site: quotationDetails.site || "-",
                    quotationNo: quotationDetails.quotationNo || "-",
                    contactRef: quotationDetails.contactRef || "-",
                    designer: quotationDetails.referenceDesigner || "-",
                    isInitial: quotationDetails.isInitial || false,
                    rooms: [
                      {
                        roomName: "Budget Estimate",
                        items: quotationTableData.rows.map(r => {
                          if (r.type === 'horizontal') {
                            return {
                              isHorizontal: true,
                              name: r.text || r.particulars || ""
                            };
                          }

                          const parts = (r.particulars || "").split('\n');
                          const name = parts[0] || "";
                          const details = parts.slice(1);

                          const itemAmount = Number(r.amount) || 0;
                          const hasName = name.trim().length > 0;

                          return {
                            name: name,
                            quantity: r.quantity || "-",
                            design: r.design || "-",
                            type: "",
                            colour: r.colour || r.colourType || "-",
                            vendor: r.vendor || r.colourType || "",
                            warranty: r.warranty || "-",
                            amount: itemAmount > 0 ? `₹${itemAmount.toLocaleString('en-IN')}` : "-",
                            details: details,
                            _isEmpty: !hasName && itemAmount <= 0
                          };
                        }).filter(item => item && !item._isEmpty)
                      }
                    ],
                    totals: {
                      total: `₹${quotationTableData.subTotal.toLocaleString('en-IN')}`,
                      discount: `₹${quotationTableData.discountAmount.toLocaleString('en-IN')}`,
                      gst: `₹${quotationTableData.gstAmount.toLocaleString('en-IN')}`,
                      grandTotal: `₹${quotationTableData.grandTotal.toLocaleString('en-IN')}`,
                      discountType: quotationTableData.discountType,
                      discountValue: quotationTableData.discountValue,
                      discountLabel: quotationTableData.discountValue && Number(quotationTableData.discountValue) !== 0 ? (quotationTableData.discountType === 'percentage' ? `(${quotationTableData.discountValue}%)` : `(₹${Number(quotationTableData.discountValue).toLocaleString('en-IN')})`) : '',
                      gstType: quotationTableData.gstType,
                      gstValue: quotationTableData.gstValue,
                      gstLabel: quotationTableData.gstValue && Number(quotationTableData.gstValue) !== 0 ? (quotationTableData.gstType === 'percentage' ? `(${quotationTableData.gstValue}%)` : `(₹${Number(quotationTableData.gstValue).toLocaleString('en-IN')})`) : ''
                    }
                  }}
                />
              )}
            </div>
          }
        />
      ) : activeMainTab === 'initial_quotation' ? (
        <ClientsTable onProceedToPrep={(customer) => {
          const fullName = customer.customerName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || "";
          setQuotationDetails(prev => ({ ...prev, projectName: fullName, contactRef: getContactRefDisplay(customer), leadId: customer._id || null, clientId: customer._id || null, isInitial: true }));
          setActiveMainTab('prep');
        }} />
      ) : activeMainTab === 'ordering' ? (
        <OrderingScreen
          embedded={true}
          initialData={selectedRowForOrdering}
          onSuccess={() => {
            const rowId = selectedRowForOrdering?._id || selectedRowForOrdering?.id || selectedRowForOrdering?.contactObj?._id;
            setHighlightedRowId(rowId || null);
            setSelectedRowForOrdering(null);
            setActiveMainTab('view');
          }}
        />
      ) : (
        <BudgetTableView
          highlightedRowId={highlightedRowId}
          onStartOrdering={(data) => {
            setSelectedRowForOrdering(data);
            setActiveMainTab('ordering');
          }}
          onProceedToPrep={(contact, existingQuotation, isInitialForce) => {
            const fullName = contact.customerName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || "";
            const phone = contact.phoneNumber || contact.phone || contact.mobile || "";
            const nameWithPhone = phone ? `${fullName} - ${phone}` : fullName;

            const defaultSite = contact.city ? `${contact.city} ${contact.state || ''}`.trim() : '';

            let contactRefDesigner = '';
            if (contact.designer && contact.designer.name) {
              contactRefDesigner = contact.designer.name;
            } else if (contact.designer && typeof contact.designer === 'string') {
              contactRefDesigner = contact.designer;
            } else if (contact.referenceType || contact.referenceDetails) {
              contactRefDesigner = `${contact.referenceType || ''} ${contact.referenceDetails || ''}`.trim();
            } else if (contact.source) {
              contactRefDesigner = contact.source;
            }

            const isInitialQuote = Boolean(
              isInitialForce ||
              existingQuotation?.quotationType === 'Initial' ||
              existingQuotation?.type === 'initial_quotation' ||
              existingQuotation?.isInitial ||
              (existingQuotation?.quotationNo && typeof existingQuotation.quotationNo === 'string' && existingQuotation.quotationNo.startsWith('VK20') && existingQuotation.quotationNo.charAt(6) === 'I') ||
              contact?.quoteStatus === 'initial_quotation' ||
              contact?.teleStatus === 'initial_quotation' ||
              contact?.status === 'initial_quotation' ||
              contact?.stage === 'Initial Quotation' ||
              ['initial_quotation', 'initial quotation', 'initial', 'quote', 'quotation', 'lead', 'design_proposal', 'design proposal'].includes(String(contact?.status || contact?.leadStatus || contact?.quoteStatus || contact?.currentStage || contact?.stage || contact?.teleStatus || contact?.projectStatus || '').toLowerCase().trim())
            );

            if (existingQuotation) {
              let projName = existingQuotation.projectName;
              if (!projName || projName === fullName || projName === contact.customerName) {
                projName = nameWithPhone;
              }

              setQuotationDetails({
                _id: existingQuotation._id,
                projectName: projName,
                site: existingQuotation.site || defaultSite,
                contactRef: getContactRefDisplay(contact),
                leadId: contact._id || null,
                clientId: contact._id || null,
                date: existingQuotation.date || '',
                quotationNo: existingQuotation.quotationNo || '',
                referenceDesigner: existingQuotation.referenceDesigner || contactRefDesigner || '',
                isInitial: isInitialQuote
              });

              // Also populate quotationTableData
              const rows = Array.isArray(existingQuotation.items || existingQuotation.rows)
                ? (existingQuotation.items || existingQuotation.rows).map((r, i) => ({
                  id: r.id || i + 1,
                  particulars: r.name || r.particulars || '',
                  quantity: r.quantity || '',
                  design: r.design || '',
                  type: r.isHorizontal ? 'horizontal' : (r.type || 'item'),
                  colourType: r.colour || r.colourType || '',
                  warranty: r.warranty || '',
                  amount: r.amount || 0,
                  _fromStock: Boolean(r._fromStock)
                }))
                : [];

              const discountStr = (existingQuotation.totals?.discount || "0").replace(/[^0-9.]/g, '');
              const gstStr = (existingQuotation.totals?.gst || "0").replace(/[^0-9.]/g, '');

              setQuotationTableData(prev => ({
                ...prev,
                rows: rows.length ? rows : [{ id: 1, particulars: '', design: '', type: 'item', colourType: '', warranty: '', amount: 0 }],
                discountValue: Number(discountStr),
                discountType: 'amount', // default to amount since we parse literal amount
                gstValue: Number(gstStr),
                gstType: 'amount'
              }));
            } else {
              setQuotationDetails(prev => ({
                ...prev,
                projectName: nameWithPhone,
                site: defaultSite,
                contactRef: getContactRefDisplay(contact),
                leadId: contact._id || null,
                clientId: contact._id || null,
                _id: undefined,
                quotationNo: '',
                referenceDesigner: contactRefDesigner || '',
                isInitial: isInitialQuote
              }));
              setQuotationTableData({
                rows: [],
                subTotal: 0,
                discountAmount: 0,
                gstAmount: 0,
                grandTotal: 0,
                discountType: 'percentage',
                discountValue: 0,
                gstType: 'percentage',
                gstValue: 0
              });
            }
            setActiveMainTab('prep');
          }}
          onViewClient={(contact) => setDetail(contact)}
        />
      )}

      {/* Client Modal View */}
      {detail && (
        <ContactModals
          detail={detail}
          setDetail={setDetail}
          activities={activities}
          actNote={actNote}
          setActNote={setActNote}
          onAddActivity={async (note) => {
            const noteStr = typeof note === 'string' ? note : (note?.note || note?.comment || actNote);
            await addActivity({ note: noteStr || actNote });
            setActNote("");
          }}
          user={user}
        />
      )}

      {/* Purchase Detail Modal (Stock view) */}
      {viewPurchase && (
        <ItemDetailsModal
          item={viewPurchase}
          onClose={() => setViewPurchase(null)}
        />
      )}

      {/* Completion Modal when navigated from Lead Execution */}
      {completedExecutionReturnId && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 flex flex-col items-center text-center border border-slate-200">
            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 size={36} />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-2 font-poppins">Quotation Completed!</h3>
            <p className="text-sm text-slate-500 mb-6 font-poppins">
              Your budget quotation has been successfully created/updated. Click Continue below to redirect right back to Lead Execution.
            </p>
            <Button
              variant="orange"
              className="w-full justify-center py-3 text-base font-semibold font-poppins"
              onClick={() => navigate(`/lead-executions/${completedExecutionReturnId}`)}
            >
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
